import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useRef, useState } from 'react'
import { LinkButton } from '~/features/navigation'
import { useCurrentOrganization } from '~/hooks'
import { FileUploadMutators } from '~/mutators/file-uploads'
import { ScorecardMutators } from '~/mutators/scorecards'
import {
  WizardContent,
  WizardHeading,
  WizardTemplate,
  WizardTitle,
} from '~/templates/wizard'

import { Button, Heading1, Icon, IconTypes } from '@hap/components'

export const Route = createFileRoute('/_authenticated/$alias/_app/scorecard/new/')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath })
  const organization = useCurrentOrganization()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [mode, setMode] = useState<'options' | 'processing'>('options')
  const [fileName, setFileName] = useState<string>('')

  const { create, isPending: isCreating } = ScorecardMutators.useCreate({
    onSuccess: (scorecardId) => {
      navigate({
        to: '/$alias/scorecard/$scorecardId/edit',
        params: { scorecardId },
      })
    },
  })

  const { upload, isPending: isUploading } = FileUploadMutators.useFileUpload({
    associatedCollection: 'organizations',
    associatedWith: organization?.id ?? '',
    cropToSquare: false,
  })

  const isBusy = isCreating || isUploading

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.item(0)
      if (!file) return

      setFileName(file.name)
      setMode('processing')

      try {
        const fileId = await upload(file)
        const scorecardName = humanizeFileName(file.name)
        await create({
          name: scorecardName,
          resources: [{ type: 'file-upload', id: fileId }],
        })
      } catch {
        setMode('options')
        setFileName('')
      }

      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [upload, create],
  )

  const handleCancel = useCallback(() => {
    setMode('options')
    setFileName('')
  }, [])

  return (
    <WizardTemplate title="New Scorecard">
      <WizardHeading>
        <WizardTitle
          breadcrumbs={[
            {
              to: '/$alias/scorecards',
              from: Route.fullPath,
              icon: 'sidebar',
              children: 'Scorecards',
            },
            { children: 'New Scorecard' },
          ]}
        />
      </WizardHeading>
      <WizardContent>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />

        {mode === 'options' ? (
          <div className="flex flex-col gap-10">
            <header className="mb-8 text-center">
              <Heading1>How would you like to create a new scorecard?</Heading1>
            </header>

            <div className="grid grid-cols-3 gap-2">
              <Option
                label="Select a Template"
                icon="copy"
                onClick={() =>
                  navigate({
                    to: '/$alias/scorecard/new/template',
                    from: Route.fullPath,
                  })
                }
                disabled={isBusy}
              />
              <Option
                label="Upload an existing"
                icon="upload-simple"
                onClick={handleUploadClick}
                disabled={isBusy}
              />
              <Option
                label="Start from scratch"
                icon="plus"
                onClick={() => create()}
                disabled={isBusy}
              />
            </div>

            <LinkButton
              variant="secondary"
              from={Route.fullPath}
              size="lg"
              to="/$alias/scorecards"
            >
              Cancel
            </LinkButton>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6">
            <Icon name="arrows-clockwise" className="size-12 animate-spin" />
            <div className="text-center">
              <p className="font-medium">{fileName}</p>
              <p className="text-muted-foreground text-sm">
                {isUploading ? 'Uploading file...' : 'Creating scorecard...'}
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isUploading || isCreating}
            >
              Cancel
            </Button>
          </div>
        )}
      </WizardContent>
    </WizardTemplate>
  )
}

function Option(props: {
  label: string
  icon: IconTypes
  onClick?: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={props.onClick}
      disabled={props.disabled}
      className="group bg-background flex size-60 flex-col items-center justify-center rounded-lg border-0 font-medium transition-transform hover:scale-102 disabled:opacity-50 disabled:hover:scale-100"
    >
      <div className="bg-lime-green border-foreground mb-6 flex items-center justify-center rounded-md p-6">
        <Icon name={props.icon} className="size-12" weight="light" />
      </div>
      <div className="text-accent-foreground group-hover:text-foreground">
        {props.label}
      </div>
    </button>
  )
}

function humanizeFileName(fileName: string): string {
  // Remove extension
  const base = fileName.replace(/\.[^/.]+$/, '')
  // Replace dashes, underscores, and dots with spaces
  const spaced = base.replace(/[-_.]/g, ' ')
  // Title case each word
  return spaced
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim()
}

const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.csv',
  '.md',
]
